import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  Users, 
  Trophy, 
  Calendar, 
  Gamepad2, 
  TrendingUp, 
  DollarSign,
  Activity,
  Award,
  Newspaper,
  MessageSquare
} from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import dashboardService from '../../services/dashboardService';

const DashboardContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const WelcomeSection = styled.div`
  margin-bottom: 30px;
`;

const WelcomeTitle = styled.h1`
  color: #ffffff;
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 10px 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const WelcomeSubtitle = styled.p`
  color: #b8b8d1;
  font-size: 18px;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 25px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.gradient || 'linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%)'};
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => props.gradient || 'linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
`;

const StatValue = styled.div`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
`;

const StatLabel = styled.div`
  color: #b8b8d1;
  font-size: 14px;
  margin-top: 5px;
`;

const StatChange = styled.div`
  color: ${props => props.positive ? '#4ecdc4' : '#ff6b6b'};
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const ActionCard = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  }
`;

const ActionIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px;
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
`;

const ActionTitle = styled.h3`
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 10px 0;
`;

const ActionDescription = styled.p`
  color: #b8b8d1;
  font-size: 14px;
  margin: 0;
`;

const RecentActivity = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 8px;
  margin-bottom: 10px;
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 5px;
`;

const ActivityTime = styled.div`
  color: #6c6c8a;
  font-size: 12px;
`;

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalPlayers: 0,
    newPlayersToday: 0,
    activeMatches: 0,
    upcomingMatches: 0,
    totalRevenue: 0,
    revenueToday: 0,
    pendingNewsArticles: 0,
    openSupportTickets: 0,
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await dashboardService.getDashboardStats();
        setStats({
          totalPlayers: data.totalPlayers || 0,
          newPlayersToday: data.newPlayersToday || 0,
          activeMatches: data.activeMatches || 0,
          upcomingMatches: data.upcomingMatches || 0,
          totalRevenue: data.totalRevenue || 0,
          revenueToday: data.revenueToday || 0,
          pendingNewsArticles: data.pendingNewsArticles || 0,
          openSupportTickets: data.openSupportTickets || 0,
        });
      } catch (e) {
        // Keep defaults on error
        console.error('Failed to load dashboard stats', e);
      }
    };
    loadStats();
  }, []);

  const [recentActivities] = useState([
    { id: 1, title: 'تورنومنت جدید ایجاد شد', time: '2 ساعت پیش', icon: Trophy },
    { id: 2, title: '5 بازیکن جدید ثبت نام کردند', time: '4 ساعت پیش', icon: Users },
    { id: 3, title: 'مسابقه نهایی تکمیل شد', time: '6 ساعت پیش', icon: Calendar },
    { id: 4, title: 'بازی جدید اضافه شد', time: '1 روز پیش', icon: Gamepad2 },
  ]);

  const quickActions = [
    {
      title: 'ایجاد تورنومنت',
      description: 'تورنومنت جدید بسازید',
      icon: Trophy,
      action: () => console.log('Create tournament'),
    },
    {
      title: 'مدیریت بازیکنان',
      description: 'بازیکنان را مدیریت کنید',
      icon: Users,
      action: () => console.log('Manage players'),
    },
    {
      title: 'برنامه‌ریزی مسابقات',
      description: 'مسابقات جدید برنامه‌ریزی کنید',
      icon: Calendar,
      action: () => console.log('Schedule matches'),
    },
    {
      title: 'تنظیمات سیستم',
      description: 'تنظیمات سیستم را تغییر دهید',
      icon: Activity,
      action: () => console.log('System settings'),
    },
  ];

  return (
    <DashboardContainer>
      <WelcomeSection>
        <WelcomeTitle>خوش آمدید!</WelcomeTitle>
        <WelcomeSubtitle>پنل مدیریت تورنومنت بازی</WelcomeSubtitle>
      </WelcomeSection>

      <StatsGrid>
        <StatCard gradient="linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%)" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)">
              <Users size={24} color="white" />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalPlayers.toLocaleString('fa-IR')}</StatValue>
          <StatLabel>کل بازیکنان</StatLabel>
        </StatCard>

        <StatCard gradient="linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%)" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)">
              <Calendar size={24} color="white" />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.activeMatches.toLocaleString('fa-IR')}</StatValue>
          <StatLabel>مسابقات فعال</StatLabel>
        </StatCard>

        <StatCard gradient="linear-gradient(90deg, #ffd93d 0%, #ff8e53 100%)" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #ffd93d 0%, #ff8e53 100%)">
              <Calendar size={24} color="white" />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.upcomingMatches.toLocaleString('fa-IR')}</StatValue>
          <StatLabel>مسابقات آینده</StatLabel>
        </StatCard>

        <StatCard gradient="linear-gradient(90deg, #667eea 0%, #764ba2 100%)" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
              <DollarSign size={24} color="white" />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.totalRevenue.toLocaleString('fa-IR')}</StatValue>
          <StatLabel>درآمد کل (تومان)</StatLabel>
        </StatCard>

        <StatCard gradient="linear-gradient(90deg, #ff6b6b 0%, #ff8e8e 100%)" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)">
              <DollarSign size={24} color="white" />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.revenueToday.toLocaleString('fa-IR')}</StatValue>
          <StatLabel>درآمد امروز (تومان)</StatLabel>
        </StatCard>

        <StatCard gradient="linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%)" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)">
              <Newspaper size={24} color="white" />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.pendingNewsArticles.toLocaleString('fa-IR')}</StatValue>
          <StatLabel>اخبار در انتظار</StatLabel>
        </StatCard>

        <StatCard gradient="linear-gradient(90deg, #00d4ff 0%, #4ecdc4 100%)" whileHover={{ scale: 1.05 }} transition={{ duration: 0.2 }}>
          <StatHeader>
            <StatIcon gradient="linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%)">
              <MessageSquare size={24} color="white" />
            </StatIcon>
          </StatHeader>
          <StatValue>{stats.openSupportTickets.toLocaleString('fa-IR')}</StatValue>
          <StatLabel>تیکت‌های باز</StatLabel>
        </StatCard>
      </StatsGrid>

      <Card title="عملیات سریع" icon={<Award size={20} />}>
        <QuickActions>
          {quickActions.map((action, index) => (
            <ActionCard
              key={index}
              onClick={action.action}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <ActionIcon>
                <action.icon size={24} color="white" />
              </ActionIcon>
              <ActionTitle>{action.title}</ActionTitle>
              <ActionDescription>{action.description}</ActionDescription>
            </ActionCard>
          ))}
        </QuickActions>
      </Card>

      <RecentActivity>
        <Card title="فعالیت‌های اخیر" icon={<Activity size={20} />}>
          {recentActivities.map((activity) => (
            <ActivityItem key={activity.id}>
              <ActivityIcon>
                <activity.icon size={20} color="white" />
              </ActivityIcon>
              <ActivityContent>
                <ActivityTitle>{activity.title}</ActivityTitle>
                <ActivityTime>{activity.time}</ActivityTime>
              </ActivityContent>
            </ActivityItem>
          ))}
        </Card>

        <Card title="آمار عملکرد" icon={<TrendingUp size={20} />}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', color: '#00d4ff', fontWeight: 'bold', marginBottom: '10px' }}>
              98%
            </div>
            <div style={{ color: '#b8b8d1', fontSize: '16px' }}>
              رضایت کاربران
            </div>
            <div style={{ marginTop: '20px', fontSize: '14px', color: '#6c6c8a' }}>
              عملکرد سیستم در بالاترین سطح
            </div>
          </div>
        </Card>
      </RecentActivity>
    </DashboardContainer>
  );
};

export default Dashboard;
